import React, { useRef, useCallback, useEffect, useState } from 'react';

interface CompactBrowserViewerProps {
  screenshotUrl: string | null;
  currentUrl: string;
  pageTitle: string;
  projectName: string;
  isConnected: boolean;
  isManualInterventionRequired?: boolean;
  manualInterventionReason?: string | null;
  isRecordingCorrections?: boolean;
  capturedCorrectionsCount?: number;
  sendMouseAction?: (actionType: string, x: number, y: number, button?: string, clickCount?: number, deltaX?: number, deltaY?: number) => void;
  sendKeyboardAction?: (actionType: string, key?: string, text?: string, modifiers?: string[]) => void;
  dismissManualIntervention?: () => void;
  startCorrectionRecording?: () => void;
  stopCorrectionRecording?: () => void;
  hideFullscreenButton?: boolean;
  onFullscreen?: () => void;
  // Tailwind classes for the screenshot area; override to render a larger in-page viewer.
  heightClass?: string;
}

const CompactBrowserViewer: React.FC<CompactBrowserViewerProps> = ({
  screenshotUrl,
  currentUrl,
  pageTitle,
  projectName,
  isConnected,
  isManualInterventionRequired = false,
  manualInterventionReason = null,
  isRecordingCorrections = false,
  capturedCorrectionsCount = 0,
  sendMouseAction,
  sendKeyboardAction,
  dismissManualIntervention,
  startCorrectionRecording,
  stopCorrectionRecording,
  hideFullscreenButton = false,
  onFullscreen,
  heightClass = 'aspect-video lg:aspect-auto lg:h-[450px]'
}) => {
  // Enable manual control when in manual intervention OR recording corrections
  const isManualControlEnabled = isManualInterventionRequired || isRecordingCorrections;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const lastPositionSentRef = useRef({ x: 0, y: 0 });
  const positionThrottleRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate coordinates relative to the actual browser viewport
  // Account for object-contain letterboxing
  const getImageCoordinates = useCallback((event: React.MouseEvent, containerRef?: React.RefObject<HTMLDivElement>) => {
    const ref = containerRef || overlayRef;
    if (!ref.current) return { x: 0, y: 0 };

    const imgElement = ref.current.querySelector('img') as HTMLImageElement;
    if (!imgElement) return { x: 0, y: 0 };

    const imgRect = imgElement.getBoundingClientRect();

    // Get the natural dimensions of the image (screenshot size)
    const naturalWidth = imgElement.naturalWidth || 1280;
    const naturalHeight = imgElement.naturalHeight || 720;

    // Browser viewport dimensions (actual browser size)
    const actualWidth = 1920;
    const actualHeight = 1080;

    // Calculate the actual rendered image dimensions within the container
    // object-contain maintains aspect ratio, so we need to find the actual rendered area
    const containerWidth = imgRect.width;
    const containerHeight = imgRect.height;

    const imageAspect = naturalWidth / naturalHeight;
    const containerAspect = containerWidth / containerHeight;

    let renderedWidth: number;
    let renderedHeight: number;
    let offsetX = 0;
    let offsetY = 0;

    if (containerAspect > imageAspect) {
      // Container is wider than image - letterboxing on sides
      renderedHeight = containerHeight;
      renderedWidth = containerHeight * imageAspect;
      offsetX = (containerWidth - renderedWidth) / 2;
    } else {
      // Container is taller than image - letterboxing on top/bottom
      renderedWidth = containerWidth;
      renderedHeight = containerWidth / imageAspect;
      offsetY = (containerHeight - renderedHeight) / 2;
    }

    // Calculate mouse position relative to the container
    const mouseX = event.clientX - imgRect.left;
    const mouseY = event.clientY - imgRect.top;

    // Adjust for letterboxing offset
    const relativeX = mouseX - offsetX;
    const relativeY = mouseY - offsetY;

    // Check if click is within the actual image area
    if (relativeX < 0 || relativeX > renderedWidth || relativeY < 0 || relativeY > renderedHeight) {
      console.log('🖱️ Click outside image area');
      return { x: 0, y: 0 };
    }

    // Scale from rendered size to actual browser viewport
    const scaleX = actualWidth / renderedWidth;
    const scaleY = actualHeight / renderedHeight;

    const x = Math.round(relativeX * scaleX);
    const y = Math.round(relativeY * scaleY);

    const clampedX = Math.max(0, Math.min(x, actualWidth));
    const clampedY = Math.max(0, Math.min(y, actualHeight));

    console.log('🖱️ Coordinate calculation:', {
      mouse: { mouseX, mouseY },
      offset: { offsetX, offsetY },
      relative: { relativeX, relativeY },
      rendered: { renderedWidth, renderedHeight },
      scale: { scaleX, scaleY },
      final: { x: clampedX, y: clampedY }
    });

    return { x: clampedX, y: clampedY };
  }, []);

  // Handle mouse click (for regular view)
  const handleClick = useCallback((event: React.MouseEvent) => {
    if (!isManualControlEnabled || !sendMouseAction || !isConnected) return;

    event.preventDefault();
    const coords = getImageCoordinates(event);
    const button = event.button === 0 ? 'left' : event.button === 1 ? 'middle' : 'right';
    const clickCount = event.detail || 1;

    console.log('🖱️ [CompactBrowserViewer] Sending mouse_click:', { ...coords, button, clickCount });
    sendMouseAction('mouse_click', coords.x, coords.y, button, clickCount);
  }, [isManualControlEnabled, sendMouseAction, isConnected, getImageCoordinates]);

  // Handle scroll (wheel events)
  const handleWheel = useCallback((event: React.WheelEvent) => {
    if (!isManualControlEnabled || !sendMouseAction || !isConnected) return;

    event.preventDefault();
    const coords = getImageCoordinates(event as unknown as React.MouseEvent);

    console.log('🖱️ [CompactBrowserViewer] Sending scroll:', { ...coords, deltaX: event.deltaX, deltaY: event.deltaY });
    sendMouseAction('scroll', coords.x, coords.y, undefined, undefined, event.deltaX, event.deltaY);
  }, [isManualControlEnabled, sendMouseAction, isConnected, getImageCoordinates]);

  // Handle key down
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!isManualControlEnabled || !sendKeyboardAction || !isConnected) return;

    // For printable characters, send as text input
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      console.log('⌨️ [CompactBrowserViewer] Sending text_input:', { text: event.key });
      sendKeyboardAction('text_input', undefined, event.key);
      return;
    }

    const isModifierKey = ['Control', 'Shift', 'Alt', 'Meta'].includes(event.key);
    if (!isModifierKey) {
      event.preventDefault();
    }

    const modifiers: string[] = [];
    if (event.ctrlKey) modifiers.push('ctrl');
    if (event.shiftKey) modifiers.push('shift');
    if (event.altKey) modifiers.push('alt');
    if (event.metaKey) modifiers.push('meta');

    console.log('⌨️ [CompactBrowserViewer] Sending key_down:', { key: event.key, code: event.code, modifiers });
    sendKeyboardAction('key_down', event.key, undefined, modifiers);
  }, [isManualControlEnabled, sendKeyboardAction, isConnected]);

  // Auto-focus the (single, inline) overlay when manual control is enabled — native fullscreen
  // keeps this same element on screen, so there is no separate fullscreen overlay to focus.
  useEffect(() => {
    if (isManualControlEnabled && overlayRef.current) {
      overlayRef.current.focus();
    }
  }, [isManualControlEnabled, isFullscreen]);

  // Track the browser's native fullscreen state (Esc/F11 exit it for free — no custom key handling).
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => {
      if (positionThrottleRef.current) {
        clearTimeout(positionThrottleRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={`bg-gray-50 dark:bg-slate-900/50 border-2 rounded-lg overflow-hidden ${
        isFullscreen ? 'flex flex-col h-screen w-screen !rounded-none bg-black dark:bg-black' : ''
      } ${
      isRecordingCorrections
        ? 'border-amber-500 shadow-lg shadow-amber-500/30'
        : isManualInterventionRequired
        ? 'border-red-500 shadow-lg shadow-red-500/30'
        : 'border-gray-200 dark:border-slate-700/50'
    }`}>
      {/* Header with URL */}
      <div className={`px-4 py-2 border-b ${
        isManualInterventionRequired
          ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50'
          : 'bg-gray-100 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700/50'
      }`}>
        <div className="flex items-center space-x-2">
          {/* Connection Status or Manual Intervention Warning */}
          {isManualInterventionRequired ? (
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16v2h2v-2h-2zm0-6v4h2v-4h-2z"/>
              </svg>
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">Manual Action Required</span>
            </div>
          ) : (
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 dark:bg-green-400 animate-pulse' : 'bg-gray-400 dark:bg-gray-500'}`} />
          )}

          {/* URL */}
          <div className="flex-1 min-w-0">
            <div className={`text-xs truncate ${
              isManualInterventionRequired ? 'text-red-600 dark:text-red-300' : 'text-gray-600 dark:text-slate-400'
            }`}>
              {currentUrl || projectName}
            </div>
            {pageTitle && (
              <div className="text-xs text-gray-500 dark:text-slate-500 truncate">
                {pageTitle}
              </div>
            )}
          </div>

          {/* Fullscreen Button — external handler if provided (QA uses FullscreenTaskViewer),
              otherwise the browser-native Fullscreen API on this component (no popup overlay). */}
          {screenshotUrl && !hideFullscreenButton && (
            <button
              onClick={() =>
                onFullscreen
                  ? onFullscreen()
                  : isFullscreen
                    ? document.exitFullscreen?.()
                    : rootRef.current?.requestFullscreen?.()
              }
              className="p-1 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700/50 rounded transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isFullscreen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V5m0 4H5m4 0L4 4m11 5h4m-4 0V5m0 4l5-5M9 15v4m0-4H5m4 0l-5 5m11-5h4m-4 0v4m0-4l5 5" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                )}
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Screenshot Display - use aspect-ratio on mobile, fixed height on desktop; fill the
          viewport in native fullscreen (flex child of the h-screen root). */}
      <div className={`relative bg-gray-100 dark:bg-slate-950/50 ${isFullscreen ? 'flex-1 min-h-0' : heightClass}`}>
        {screenshotUrl ? (
          <>
            {/* Manual Intervention Warning Banner */}
            {isManualInterventionRequired && manualInterventionReason && (
              <div className="absolute top-0 left-0 right-0 bg-red-900/90 border-b border-red-700 px-4 py-2 z-10">
                <p className="text-xs text-red-100">
                  <span className="font-semibold">Agent needs help:</span> {manualInterventionReason}
                </p>
              </div>
            )}

            {/* Browser Screenshot with Manual Control */}
            <div
              ref={overlayRef}
              className={`w-full h-full ${isManualControlEnabled ? 'cursor-crosshair' : ''}`}
              onClick={handleClick}
              onWheel={handleWheel}
              onKeyDown={handleKeyDown}
              tabIndex={isManualControlEnabled ? 0 : -1}
              style={{
                userSelect: isManualControlEnabled ? 'none' : 'auto'
              }}
            >
              <img
                src={screenshotUrl}
                alt="Browser screenshot"
                className="w-full h-full object-contain"
              />

              {/* Teaching Mode Overlay - when recording corrections */}
              {isRecordingCorrections && (
                <>
                  {/* Top overlay with message */}
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-amber-900/90 to-transparent p-3 z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-300"></span>
                        </span>
                        <span className="text-amber-100 text-sm font-medium">Your turn — click the correct element</span>
                        {capturedCorrectionsCount > 0 && (
                          <span className="text-amber-300 text-xs">({capturedCorrectionsCount} recorded)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Done button */}
                  <div className="absolute bottom-3 right-3 z-20">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        stopCorrectionRecording?.();
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg shadow-lg transition-all flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      <span>Done</span>
                    </button>
                  </div>
                </>
              )}

              {/* Take Over Button - floating on browser when not in any special mode */}
              {!isRecordingCorrections && !isManualInterventionRequired && startCorrectionRecording && (
                <div className="absolute top-2 right-2 z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startCorrectionRecording();
                    }}
                    className="px-2.5 py-1.5 bg-slate-800/80 hover:bg-slate-700/90 text-slate-200 text-xs font-medium rounded-lg shadow-lg transition-all flex items-center space-x-1.5 backdrop-blur-sm border border-slate-600/50"
                    title="Agent wrong? Click to show it the right action"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                    </svg>
                    <span>Take Over</span>
                  </button>
                </div>
              )}

              {/* Manual Intervention Active Indicator */}
              {isManualInterventionRequired && !isRecordingCorrections && (
                <div className="absolute top-2 right-2 bg-green-600 bg-opacity-80 text-white text-xs px-2 py-1 rounded z-10">
                  🖱️⌨️ Manual Control Active
                </div>
              )}
            </div>

            {/* Resume Button */}
            {isManualInterventionRequired && dismissManualIntervention && (
              <div className="absolute bottom-4 right-4 z-20">
                <button
                  onClick={dismissManualIntervention}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg shadow-lg transition-colors"
                >
                  Resume Agent
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-500 dark:text-slate-500">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3z" />
              </svg>
              <p className="text-sm">Waiting for browser stream...</p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default CompactBrowserViewer;
